export type TreatmentDisplayStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'DONE'
  | 'CLOSE'
  | 'CANCEL'

type TreatmentStatusItem = {
  id?: number | string
  status?: string
}

export type TreatmentStatusInput = {
  planStatus?: string
  items?: TreatmentStatusItem[]
  activeTreatmentIds?: Array<number | string>
}

type TreatmentPlanForDisplaySort = {
  status?: string
  createdAt?: string | number | Date | null
  patientTreatmentPlanItems?: TreatmentStatusItem[]
}

export function deriveTreatmentDisplayStatus({
  planStatus,
  items = [],
  activeTreatmentIds = [],
}: TreatmentStatusInput): TreatmentDisplayStatus {
  if (planStatus === 'CANCEL') return 'CANCEL'
  if (planStatus === 'CLOSE') return 'CLOSE'
  if (planStatus === 'DONE') return 'DONE'

  const planItems = Array.isArray(items) ? items : []
  if (planItems.length > 0 && planItems.every((item) => item.status === 'DONE')) {
    return 'DONE'
  }

  const activeIds = activeTreatmentIds.map((id) => String(id))
  if (
    planStatus === 'IN_PROGRESS' ||
    planItems.some((item) => item.status === 'DONE') ||
    planItems.some((item) => item.id != null && activeIds.includes(String(item.id)))
  ) {
    return 'IN_PROGRESS'
  }

  return 'PENDING'
}

/** Sort Tx.P plans by the status users see, keeping unfinished work on top. */
export function sortTreatmentPlansByDisplayStatus<T extends TreatmentPlanForDisplaySort>(
  plans: T[] = [],
  activeTreatmentIds: Array<number | string> = [],
): T[] {
  return plans
    .map((plan, index) => {
      const status = deriveTreatmentDisplayStatus({
        planStatus: plan.status,
        items: plan.patientTreatmentPlanItems,
        activeTreatmentIds,
      })
      const createdAt = plan.createdAt == null ? Number.NaN : new Date(plan.createdAt).getTime()

      return {
        plan,
        index,
        activeGroup: status === 'PENDING' || status === 'IN_PROGRESS' ? 0 : 1,
        createdAt,
      }
    })
    .sort((left, right) => {
      if (left.activeGroup !== right.activeGroup) return left.activeGroup - right.activeGroup

      const leftHasDate = Number.isFinite(left.createdAt)
      const rightHasDate = Number.isFinite(right.createdAt)
      if (leftHasDate && rightHasDate && left.createdAt !== right.createdAt) {
        return right.createdAt - left.createdAt
      }
      if (leftHasDate !== rightHasDate) return leftHasDate ? -1 : 1

      return left.index - right.index
    })
    .map(({ plan }) => plan)
}

/**
 * Keep the legacy Tx.C status derivation separate from the Tx.P rules.
 * Tx.C historically treated an empty item list as DONE because it used
 * lodash's _.every directly; changing that behavior can alter course labels.
 */
export function deriveTreatmentCourseDisplayStatus({
  planStatus,
  items = [],
  activeTreatmentIds = [],
}: TreatmentStatusInput): TreatmentDisplayStatus {
  if (planStatus === 'CANCEL') return 'CANCEL'
  if (planStatus === 'CLOSE') return 'CLOSE'
  if (planStatus === 'DONE') return 'DONE'

  const courseItems = Array.isArray(items) ? items : []
  if (courseItems.every((item) => item.status === 'DONE')) return 'DONE'

  const activeIds = activeTreatmentIds.map((id) => String(id))
  if (
    courseItems.some((item) => item.status === 'DONE') ||
    courseItems.some((item) => item.id != null && activeIds.includes(String(item.id)))
  ) {
    return 'IN_PROGRESS'
  }

  return 'PENDING'
}

/** Select the TDH Tx.P rules without changing the legacy Tx.C rules. */
export function deriveTreatmentStatusForTheme(
  input: TreatmentStatusInput,
  isTdhTxp: boolean,
): TreatmentDisplayStatus {
  return (isTdhTxp ? deriveTreatmentDisplayStatus : deriveTreatmentCourseDisplayStatus)(input)
}

/** Keep the TDH treatment-plan document status identical to the Tx.P list status. */
export function getTreatmentPlanDocumentStatus(input: TreatmentStatusInput): TreatmentDisplayStatus {
  return deriveTreatmentStatusForTheme(input, true)
}

export function getTreatmentStatusTranslationKey(
  status: TreatmentDisplayStatus | string,
  isTreatmentPlan: boolean,
): string {
  return `TX_INFORMATION.${isTreatmentPlan ? 'STATUS_TXP' : 'STATUS'}.${status}`
}

type TreatmentPlanSummaryItem = {
  refId?: number | string | null
}

export type TreatmentPlanItemAddDisabledInput = {
  planStatus?: string
  itemStatus?: string
  isAdding?: boolean
  isAlreadyInSummary?: boolean
}

/** Match plan-item ids consistently when API and Redux use different primitive types. */
export function isTreatmentPlanItemInSummary(
  summaryItems: TreatmentPlanSummaryItem[] = [],
  itemId?: number | string | null,
): boolean {
  if (itemId == null || !Array.isArray(summaryItems)) return false

  return summaryItems.some((item) => (
    item?.refId != null && String(item.refId) === String(itemId)
  ))
}

/**
 * An existing unfinished plan item can be added to treatment after reopening
 * the plan. Creating a new plan is permission-gated elsewhere and must not
 * disable this action.
 */
export function isTreatmentPlanItemAddDisabled({
  planStatus,
  itemStatus,
  isAdding = false,
  isAlreadyInSummary = false,
}: TreatmentPlanItemAddDisabledInput): boolean {
  return isAdding ||
    planStatus === 'CLOSE' ||
    planStatus === 'CANCEL' ||
    planStatus === 'DONE' ||
    itemStatus === 'DONE' ||
    isAlreadyInSummary
}

export type TreatmentPlanEditDisabledInput = {
  planStatus?: string
  allItemsDone?: boolean
  hasSavedSignature?: boolean
}

/** A closed or finalized plan must remain read-only after it is saved. */
export function isTreatmentPlanEditDisabled({
  planStatus,
  allItemsDone = false,
  hasSavedSignature = false,
}: TreatmentPlanEditDisabledInput): boolean {
  return planStatus === 'CLOSE' ||
    planStatus === 'CANCEL' ||
    planStatus === 'DONE' ||
    allItemsDone ||
    hasSavedSignature
}

export type TreatmentPlanItemEditDisabledInput = TreatmentPlanEditDisabledInput & {
  itemStatus?: string
  isAlreadyInSummary?: boolean
}

/** Only unfinished items that are not already in the treatment summary can be edited. */
export function isTreatmentPlanItemEditDisabled({
  itemStatus,
  isAlreadyInSummary = false,
  ...planInput
}: TreatmentPlanItemEditDisabledInput): boolean {
  return isTreatmentPlanEditDisabled(planInput) ||
    itemStatus === 'DONE' ||
    isAlreadyInSummary
}

type TxpDoctor = {
  userId?: number
  fullname?: string
}

type TxpDoctorLabelInput = {
  doctorId?: number | null
  doctors?: TxpDoctor[]
  creatorName?: unknown
  creatorLabel: string
}

const toDisplayName = (value: unknown): string => {
  if (typeof value === 'string') return value.trim()
  if (!value || typeof value !== 'object') return ''

  const person = value as { fullname?: unknown; name?: unknown; username?: unknown }
  return toDisplayName(person.fullname || person.name || person.username)
}

/**
 * Use the assigned doctor when available. Legacy Tx.P rows can have doctorId=0,
 * so show the plan creator explicitly instead of implying that the doctor data
 * is missing.
 */
export function getTxpDoctorLabel({
  doctorId,
  doctors = [],
  creatorName,
  creatorLabel,
}: TxpDoctorLabelInput): string {
  const doctor = doctors.find((item) => String(item.userId) === String(doctorId))
  const doctorName = toDisplayName(doctor?.fullname)
  if (doctorName) return doctorName

  const creator = toDisplayName(creatorName)
  return creator ? `${creator} (${creatorLabel})` : '-'
}

/** Default new Tx.P rows to the current user only when that user is a doctor. */
export function getTxpDefaultDoctorId(
  currentUserId: number | null | undefined,
  doctors: TxpDoctor[] = [],
): number {
  if (!currentUserId) return 0
  return doctors.some((doctor) => String(doctor.userId) === String(currentUserId))
    ? currentUserId
    : 0
}

/** Prefer item-level information and fall back to the plan-level remark. */
export function getTxpAdditionalNote(itemRemark?: unknown, planRemark?: unknown): string {
  return toDisplayName(itemRemark) || toDisplayName(planRemark)
}
